import { Button, Card, CardActions, CardContent, Chip, Stack, TextField, Typography } from '@ethings/ui-toolkit';
import { Popover, Tooltip } from '@mui/material';
import { grey } from '@mui/material/colors';
import { Box } from '@mui/system';
import { ChangeEvent, FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { testid } from '../../util/test-id';
import { ConfirmDialog } from '../confirm-dialog';
import { AppliedFilter, EntityField } from '../entity-search/types';
import { useSavedFilterStyles } from './styles';

interface Props {
  filters: AppliedFilter[];
  id: string;
  isApplied: boolean;
  name: string;
  onLoadFilter: () => void;
  onRemoveFilter: () => void;
  onRenameFilter: (newName: string) => boolean;
}

const renderFilterValue = (filter: AppliedFilter): string => {
  if (filter.definition.type === 'entity') {
    return (filter.value as EntityField).display;
  }
  return String(filter.value);
};

export const SavedFilterElement: FC<Props> = ({
  filters,
  id,
  isApplied,
  name,
  onLoadFilter,
  onRemoveFilter,
  onRenameFilter,
}) => {
  const classes = useSavedFilterStyles() as Record<string, string>;
  const { t } = useTranslation(['common']);
  const [renameActive, setRenameActive] = useState(false);
  const [filterNameInput, setFilterNameInput] = useState<string>(name);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [isSubtitleHovered, setIsSubtitleHovered] = useState(false);
  const [isPopoverHovered, setIsPopoverHovered] = useState(false);
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const popoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setFilterNameInput(name);
  }, [name]);

  useEffect(() => {
    if (!renameActive) return;
    inputFieldRef.current?.focus();
  }, [renameActive]);

  const handleRename = (): void => {
    const nextName = filterNameInput.trim();
    if (!nextName) {
      setFilterNameInput(name);
      setRenameActive(false);
      return;
    }

    onRenameFilter(nextName);
    setRenameActive(false);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      inputFieldRef.current?.blur();
    }
  };

  const clearPopoverCloseTimer = (): void => {
    if (popoverCloseTimerRef.current) {
      clearTimeout(popoverCloseTimerRef.current);
      popoverCloseTimerRef.current = null;
    }
  };

  const handleSubtitleMouseEnter = (event: React.MouseEvent<HTMLElement>): void => {
    clearPopoverCloseTimer();
    setPopoverAnchor(event.currentTarget);
    setIsSubtitleHovered(true);
  };

  const handleSubtitleMouseLeave = (): void => {
    setIsSubtitleHovered(false);
  };

  const handlePopoverMouseEnter = (): void => {
    clearPopoverCloseTimer();
    setIsPopoverHovered(true);
  };

  const handlePopoverMouseLeave = (): void => {
    setIsPopoverHovered(false);
  };

  useEffect(() => {
    if (isSubtitleHovered || isPopoverHovered) {
      clearPopoverCloseTimer();
      return;
    }

    popoverCloseTimerRef.current = setTimeout(() => {
      setPopoverAnchor(null);
    }, 120);

    return () => {
      clearPopoverCloseTimer();
    };
  }, [isPopoverHovered, isSubtitleHovered]);

  useEffect(() => {
    return () => {
      clearPopoverCloseTimer();
    };
  }, []);

  const filterCount = filters.length;
  const filterCountText = filterCount === 1 ? `1 ${t('terms.filter')}` : `${filterCount} ${t('common:component.filter.heading')}`;

  return (
    <Card
      data-testid={testid`saved-filter-${id}`}
      variant="outlined"
    >
      <CardContent className={classes.filterCardContent}>
        <Box className={classes.filterNameCell}>
          {renameActive
            ? (
              <TextField
                data-testid={testid`saved-filter-${id}-name`}
                fullWidth
                inputRef={inputFieldRef}
                onBlur={handleRename}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setFilterNameInput(event.target.value)}
                onKeyDown={onInputKeyDown}
                size="small"
                value={filterNameInput}
                variant="outlined"
              />
            )
            : (
              <Tooltip title={t('common.action.rename')} arrow>
                <Box
                  onClick={() => setRenameActive(true)}
                  sx={{
                    alignSelf: 'flex-start',
                    cursor: 'pointer',
                    '&:hover p': {
                      textDecorationLine: 'underline',
                      textDecorationStyle: 'dotted',
                      textDecorationColor: grey[600],
                    },
                  }}
                >
                  <Typography variant="body1">{name}</Typography>
                </Box>
              </Tooltip>
            )
          }
        </Box>

        <Typography
          onMouseEnter={handleSubtitleMouseEnter}
          onMouseLeave={handleSubtitleMouseLeave}
          style={{ alignSelf: 'flex-start', cursor: 'pointer' }}
          variant="caption"
          color="text.secondary"
          data-testid={testid`filter-count-${id}`}
        >
          {filterCountText}
        </Typography>

      </CardContent>

      <Popover
        anchorEl={popoverAnchor}
        open={Boolean(popoverAnchor) && (isSubtitleHovered || isPopoverHovered)}
        onClose={() => undefined}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          root: {
            style: { pointerEvents: 'none' },
          },
          paper: {
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave,
            style: { pointerEvents: 'auto' },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="column" spacing={0.5}>
            {filters.map((filter, index) => (
              <Chip
                key={`${id}-popover-chip-${index}`}
                label={`${filter.definition.label}: ${renderFilterValue(filter)}`}
                size="small"
                variant="filled"
              />
            ))}
          </Stack>
        </Box>
      </Popover>

      <CardActions className={classes.filterActionsCell}>
        <Box sx={{ display: 'inline-flex' }}>
          <ConfirmDialog
            buttonColor="error"
            buttonSize="small"
            buttonVariant="text"
            confirmPrompt={<></>}
            initialPrompt={t('common:component.filter.labels.delete')}
            onConfirm={() => Promise.resolve(onRemoveFilter())}
            okText={t('common:common.action.confirm')}
          />
        </Box>
        <Button
          color="primary"
          data-testid={testid`apply-saved-filter-${id}-button`}
          disabled={isApplied}
          onClick={onLoadFilter}
          size="small"
          variant="light"
        >
          Apply
        </Button>
      </CardActions>
    </Card>
  );
};
